import React, { useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { buildChartTheme } from './chartTheme';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function Heatmap({ data, height = 260, unit = 'runs' }) {
  const wrapperRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    const node = wrapperRef.current;
    const instance = chartRef.current?.getEchartsInstance?.();

    if (!node || !instance || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => instance.resize());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const theme = buildChartTheme();

  const hours = Array.from({ length: 24 }, (_, hour) => (
    String(hour).padStart(2, '0')
  ));

  const seriesData = [];
  let max = 0;

  (data || []).forEach((dayValues, dayIndex) => {
    (dayValues || []).forEach((value, hourIndex) => {
      const safeValue = Number.isFinite(value) ? value : 0;
      max = Math.max(max, safeValue);
      seriesData.push([hourIndex, dayIndex, safeValue]);
    });
  });

  const option = {
    grid: { left: 44, right: 16, top: 12, bottom: 28 },
    tooltip: {
      position: 'top',
      backgroundColor: theme.surfaces.raised,
      borderColor: theme.surfaces.border,
      textStyle: { color: theme.surfaces.textPrimary, fontSize: 12 },
      formatter: (params) => (
        `${DAY_LABELS[params.value[1]]} ${hours[params.value[0]]}:00 · ${params.value[2]} ${unit}`
      )
    },
    xAxis: {
      type: 'category',
      data: hours,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: theme.surfaces.textSecondary, fontSize: 10, interval: 2 },
      splitArea: { show: false }
    },
    yAxis: {
      type: 'category',
      data: DAY_LABELS,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: theme.surfaces.textSecondary, fontSize: 11 }
    },
    visualMap: {
      min: 0,
      max: max || 1,
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      itemWidth: 10,
      itemHeight: 60,
      show: false,
      inRange: {
        color: [
          theme.surfaces.elevated,
          theme.colors.running
        ]
      }
    },
    series: [{
      name: unit,
      type: 'heatmap',
      data: seriesData,
      itemStyle: {
        borderColor: theme.surfaces.border,
        borderWidth: 1,
        borderRadius: 2
      },
      emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0, 0, 0, 0.4)' } }
    }]
  };

  return (
    <div className="chart-wrapper" ref={wrapperRef} style={{ height }}>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: '100%', width: '100%' }}
        notMerge
        lazyUpdate
      />
    </div>
  );
}

export default Heatmap;
